import { Box, Typography } from '@mui/material';

import Layout from '@src/components/layout/Layout';
import LayUpTable from './components/LayUpTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import SearchLayingUpBox from './components/SearchLayingUpBox';
import { useAuth } from '@src/auth/hooks/useAuth';
import useGetLayUpLazy, {
  LayUpsDTO,
  LayUpsListsDTO,
} from '@src/adapters/hooks/layUp/useGetLayup';
import TransitionsModal, {
  GroupedLayupsOperatingList,
} from './components/ProceduresModal';
import useGetLayUpOperatingList from '@src/adapters/hooks/layUp/useGetOperationList';
import { LayUpStatuses, LayUpStatusFilter } from '@src/constants/LayupStatuses';

export const FIXED_DATE_RANGE_DAYS = 7;
const REFETCH_INTERVAL = 60000;

const cardLayupStatusOrder: LayUpStatuses[] = [
  'Checked',
  'Checking',
  'CheckPending',
  'LayUpInProgress',
  'LayUpPaused',
  'Missing',
  'CheckedMissing',
];

export type GroupedLayUp = {
  status: LayUpStatuses;
  consultantId: string;
  consultantFullName: string;
  nbLayupsWithMissingItems: number;

  dateGroups: {
    procedureDate: Date;
    status: LayUpStatuses;
    hasMissingItems: boolean;
  }[];

  operatingLists: {
    id: string;
    name: string;
    code: string;
    order: number;

    dateGroups: {
      procedureDate: Date;
      status: LayUpStatuses;
      hasMissingItems: boolean;
    }[];
  }[];
};

const mapStatusFilter = (statusFilter: string) => {
  if (statusFilter === 'All') return undefined;
  if (statusFilter === 'Missing') return 'LayUpPaused';
  if (statusFilter === 'CheckedMissing') return 'Checked';
  return statusFilter;
};

function LayingUp() {
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [statusFilter, setStatusFilter] = useState<LayUpStatusFilter>('All');
  const { currentHospitalId } = useAuth();
  const { execute: findLayup } = useGetLayUpLazy();
  const [layups, setLayups] = useState<LayUpsDTO>();
  const [procedures, setProcedures] = useState<GroupedLayupsOperatingList>();
  const [modalOpen, setModalOpen] = useState(false);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { execute: getOperationList } = useGetLayUpOperatingList();

  const getData = useCallback(async () => {
    const result = await findLayup({
      hospitalId: currentHospitalId!,
      fromDate: new Date(date),
      status: mapStatusFilter(statusFilter),
    });

    setLayups(result);
  }, [findLayup, currentHospitalId, date, statusFilter]);

  useEffect(() => {
    getData();

    const interval = setInterval(() => {
      getData();
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [getData]);

  const groupedLayUps = useMemo(() => {
    if (!layups) {
      return [];
    }

    const { layUps: allLayUps, consultants, operatingLists } = layups;

    if (!allLayUps) {
      return [];
    }
    if (!consultants) {
      return [];
    }
    if (!operatingLists) {
      return [];
    }

    const activeFilter = statusFilter !== 'All' ? statusFilter : undefined;

    const layUps = activeFilter
      ? allLayUps.filter((l) => {
          switch (activeFilter) {
            case 'Missing':
              return l.hasMissingItems === true && l.status === 'LayUpPaused';
            case 'LayUpPaused':
              return !l.hasMissingItems && l.status === 'LayUpPaused';
            case 'CheckedMissing':
              return l.hasMissingItems === true && l.status === 'Checked';
            case 'Checked':
              return !l.hasMissingItems && l.status === 'Checked';
            default:
              return true;
          }
        })
      : allLayUps;

    const consultantIds = [
      ...new Set<string>(layUps.map((l) => l.consultantId)),
    ];

    const result: GroupedLayUp[] = [];

    for (const consultantId of consultantIds) {
      const consultant = consultants.find((c) => c.id === consultantId);
      if (!consultant) {
        console.log(`Consultant ${consultantId} not found`);
        return [];
      }

      const consultantLayUps = layUps.filter(
        (l) => l.consultantId === consultantId
      );

      const consultantLayUpDates = [
        ...new Set<Date>(consultantLayUps.map((l) => l.procedureDate)),
      ].sort();

      const dateGroups = consultantLayUpDates.map((d) => {
        const consultantDateLayups = consultantLayUps.filter(
          (l) => l.procedureDate === d
        );

        return {
          procedureDate: d,
          status: getPriorityForStatus(consultantDateLayups),
          hasMissingItems: consultantDateLayups.some((l) => l.hasMissingItems),
        };
      });

      const operatingListIds = [
        ...new Set<string>(consultantLayUps.map((l) => l.operatingListId)),
      ];

      const operatingListGroups = [];

      for (const operatingListId of operatingListIds) {
        const operatingList = operatingLists.find(
          (ol) => ol.id === operatingListId
        );
        if (!operatingList) {
          console.log(`Operating list ${operatingListId} not found`);
          return [];
        }

        const consultantOperatingListLayUps = consultantLayUps.filter(
          (cl) => cl.operatingListId === operatingListId
        );

        const consultantOperatingListDates = [
          ...new Set<Date>(
            consultantOperatingListLayUps.map((l) => l.procedureDate)
          ),
        ].sort();

        const operatingListDateGroups = consultantOperatingListDates.map(
          (d) => {
            const dateLayUps = consultantOperatingListLayUps.filter(
              (l) => l.procedureDate === d
            );

            return {
              procedureDate: d,
              status: getPriorityForStatus(dateLayUps),
              hasMissingItems: dateLayUps.some((l) => l.hasMissingItems),
            };
          }
        );

        operatingListGroups.push({
          id: operatingListId,
          name: operatingList.name,
          code: operatingList.code,
          order: operatingList.order,
          dateGroups: operatingListDateGroups,
        });
      }

      result.push({
        consultantId: consultant.id,
        consultantFullName: [
          consultant.title,
          consultant.firstName,
          consultant.lastName,
        ]
          .filter(Boolean)
          .join(' '),
        nbLayupsWithMissingItems: consultantLayUps.reduce(
          (acc, l) => acc + (l.hasMissingItems ? 1 : 0),
          0
        ),
        status: getPriorityForStatus(consultantLayUps),
        dateGroups,
        operatingLists: [...operatingListGroups].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        ),
      } as GroupedLayUp);
    }

    return result.sort((a, b) =>
      a.consultantFullName.localeCompare(b.consultantFullName)
    );
  }, [layups, statusFilter]);

  const handleClose = () => setModalOpen(false);
  const collapsed = expandedIds.size === 0;

  const toggleAll = () => {
    setExpandedIds(
      collapsed
        ? new Set(groupedLayUps.map((l) => l.consultantFullName))
        : new Set()
    );
  };

  const summaryDates = useMemo(() => {
    const dateSet = new Set<number>();

    for (const group of groupedLayUps) {
      for (const d of group.dateGroups) {
        dateSet.add(dayjs(d.procedureDate).startOf('day').valueOf());
      }
    }

    return [...dateSet].sort((a, b) => a - b).map((t) => new Date(t));
  }, [groupedLayUps]);

  const weekdays = useMemo(
    () =>
      Array.from({ length: FIXED_DATE_RANGE_DAYS }, (_, idx) =>
        dayjs(date).add(idx, 'day')
      ).filter((day) => summaryDates.some((d) => day.isSame(d, 'day'))),
    [date, summaryDates]
  );

  const toggleOne = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onGroupClicked = async (
    consultantId: string,
    operatingListId: string,
    date: Date
  ) => {
    const result = await getOperationList({
      hospitalId: currentHospitalId!,
      fromDate: new Date(dayjs(date).format('YYYY-MM-DD')),
      operatingListId,
      consultantId,
      status: mapStatusFilter(statusFilter) as LayUpStatusFilter | undefined,
    });

    const consultant = layups?.consultants.find((c) => c.id === consultantId);
    const operatingList = layups?.operatingLists.find(
      (o) => o.id === operatingListId
    );

    const { layUps, categoriesLists: categories } = result;

    if (!layUps?.length) {
      return;
    }

    const groupedLayUps = [];

    let status;

    for (const layUp of layUps) {
      let statusScore = 0;
      let totalQuantity = 0;
      let pickedQuantity = 0;
      let missingQuantity = 0;
      let hasMissingItems = false;
      const items = [];

      for (const item of layUp.items) {
        const isMissing =
          item.isMissingPicked ||
          item.isMissingChecked ||
          item.isMissingCheckedConfirmed;

        const currentQuantity =
          layUp.status === 'Checking'
            ? item.quantityChecked
            : item.quantityPicked;

        totalQuantity += item.quantity;

        if (isMissing) {
          missingQuantity += item.quantity;
          hasMissingItems = isMissing;
        } else {
          pickedQuantity += currentQuantity;
        }

        items.push({
          name: item.name,
          quantity: item.quantity,
          categoryName:
            categories?.find((c) => c.id === item.categoryId && !item.isOnHand)
              ?.name ?? 'Have available',
          isMissing,
        });
      }

      const itemStatus: LayUpStatuses =
        hasMissingItems && layUp.status === 'LayUpPaused'
          ? 'Missing'
          : hasMissingItems && layUp.status === 'Checked'
            ? 'CheckedMissing'
            : layUp.status;

      const score = cardLayupStatusOrder.indexOf(itemStatus);

      if (score > statusScore) {
        statusScore = score;
        status = itemStatus;
      }

      if (!status && layUp.status) {
        status = itemStatus;
      }

      groupedLayUps.push({
        layUpId: layUp.id,
        status: itemStatus,
        procedureName: layUp.procedureName ?? 'Unknown procedure',
        totalQuantity,
        pickedQuantity,
        missingQuantity,
        items: items.filter((i) => i.isMissing),
      });
    }

    setProcedures({
      status: status!,
      consultantFullName: [
        consultant?.title,
        consultant?.firstName,
        consultant?.lastName,
      ]
        .filter(Boolean)
        .join(' '),
      procedureDate: date,
      operatingList: operatingList?.name ?? '',
      layUps: groupedLayUps,
    });
    setModalOpen(true);
  };

  return (
    <Layout>
      <Box
        sx={{
          px: 2,
          mb: 2,
        }}>
        <Typography variant="h4">Laying Up Summary</Typography>

        <SearchLayingUpBox
          setDate={setDate}
          date={date}
          setStatus={setStatusFilter}
          status={statusFilter}
          handleOpen={toggleAll}
        />
        <LayUpTable
          weekdays={weekdays}
          layups={groupedLayUps}
          expandedIds={expandedIds}
          onToggle={toggleOne}
          onGroupClicked={onGroupClicked}
        />
        <TransitionsModal
          open={modalOpen}
          onClose={handleClose}
          procedures={procedures}
        />
      </Box>
    </Layout>
  );
}

export default LayingUp;

export function getPriorityForStatus(array: LayUpsListsDTO[]): string {
  let currentStatus = cardLayupStatusOrder[0];
  let currentScore = 0;

  for (const item of array) {
    const itemStatus: LayUpStatuses =
      item.hasMissingItems && item.status === 'LayUpPaused'
        ? 'Missing'
        : item.hasMissingItems && item.status === 'Checked'
          ? 'CheckedMissing'
          : item.status;

    const score = cardLayupStatusOrder.indexOf(itemStatus);

    if (score > currentScore) {
      currentScore = score;
      currentStatus = itemStatus;
    }
  }

  return currentStatus;
}
